import { z } from 'zod';

import { CREATABLE_KINDS } from '@/lib/entity-composition';
import type { EntityCreateInput, EntityKind } from '@/lib/api/domains/entities';

/*
 * Entity create-form schema (E02.F04). Mirrors the sibling provider-form
 * pattern: a Zod schema for the fields the user edits, plus a
 * `toEntityCreateInput` transform to the API input shape. It is a UX gate that
 * mirrors the domain server's TIN rule EXACTLY
 * (product-tpa-billing-domain-svc/internal/entity/tin.go `validateTIN`) so the
 * obviously-invalid draft never reaches the pre-commit confirm — the server
 * stays the authority (R21), this only spares the operator a terse post-confirm
 * rejection.
 *
 * - legalName: required, trimmed by the schema (no further transform).
 * - tin: EXACTLY nine digits, every character 0-9 — no punctuation, spaces or
 *   letters. An ITIN (nine digits, may start with 9, e.g. 912345678) is valid;
 *   the rule is purely "nine digits", so it is accepted unchanged.
 * - kind: one of the decided CREATABLE_KINDS values, derived from the single
 *   source so it cannot drift (never Software/TPA).
 */

const CREATABLE_KIND_VALUES = new Set<EntityKind>(
  CREATABLE_KINDS.map((k) => k.value),
);

export const entityCreateFormSchema = z.object({
  kind: z.custom<EntityKind>(
    (v) => CREATABLE_KIND_VALUES.has(v as EntityKind),
    {
      message: 'Choose a kind.',
    },
  ),
  legalName: z.string().trim().min(1, 'Legal name is required.'),
  tin: z
    .string()
    .regex(
      /^\d{9}$/,
      'Tax identifier must be exactly 9 digits (no spaces or dashes).',
    ),
});

export type EntityCreateFormValues = z.infer<typeof entityCreateFormSchema>;

/** Convert validated form values to the API input shape (legalName trimmed). */
export function toEntityCreateInput(
  values: EntityCreateFormValues,
): EntityCreateInput {
  return {
    kind: values.kind,
    legalName: values.legalName,
    tin: values.tin,
  };
}

/**
 * Tax-identifier input hygiene: strip every non-digit character and cap at nine
 * digits, so a pasted punctuated/spaced value ('912-34-5678 ') becomes
 * '912345678'. UI-free so the route and its unit test share one definition.
 */
export function sanitizeTin(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}
